/**
 * Table Column Store
 *
 * Manages column visibility, ordering, and configuration for Campaign and Organization tables.
 * Persists user preferences to localStorage using Zustand persist middleware.
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createCloudSync } from '@shared/store/zustandCloudSync';
import type {
  ColumnConfig,
  TableColumnState,
  AllTableColumnsState,
  TableType,
} from '../types/table-columns';

/**
 * Default column configurations for Campaign table
 */
const DEFAULT_CAMPAIGN_COLUMNS: TableColumnState = {
  select: {
    id: 'select',
    label: 'Select',
    visible: true,
    sortable: false,
    order: 0,
    canHide: false, // Always visible
    widthClass: 'w-10',
  },
  star: {
    id: 'star',
    label: 'Star',
    visible: true,
    sortable: false,
    order: 1,
    canHide: false, // Always visible
    widthClass: 'w-8',
  },
  campaign_name: {
    id: 'campaign_name',
    label: 'Campaign Name',
    visible: true,
    sortable: true,
    order: 2,
    canHide: true,
    widthClass: 'w-48 max-w-xs', // Limit width to prevent excessive space usage
  },
  priority: {
    id: 'priority',
    label: 'Priority',
    visible: true,
    sortable: true,
    order: 3,
    canHide: true,
    widthClass: 'w-20',
  },
  labels: {
    id: 'labels',
    label: 'Labels',
    visible: true,
    sortable: false,
    order: 4,
    canHide: true,
    widthClass: 'w-32',
  },
  tags: {
    id: 'tags',
    label: 'Tags',
    visible: true,
    sortable: false,
    order: 5,
    canHide: true,
    widthClass: 'w-32',
  },
  folder: {
    id: 'folder',
    label: 'Folder',
    visible: true,
    sortable: true,
    order: 6,
    canHide: true,
    widthClass: 'w-32',
  },
  status: {
    id: 'status',
    label: 'Status',
    visible: true,
    sortable: true,
    order: 7,
    canHide: true,
    widthClass: 'w-24',
  },
  progress_percentage: {
    id: 'progress_percentage',
    label: 'Progress',
    visible: true,
    sortable: true,
    order: 8,
    canHide: true,
    widthClass: 'w-16',
  },
  owner: {
    id: 'owner',
    label: 'Owner',
    visible: true,
    sortable: true,
    order: 9,
    canHide: true,
    widthClass: 'w-24',
  },
  last_modified: {
    id: 'last_modified',
    label: 'Last Modified',
    visible: true,
    sortable: true,
    order: 10,
    canHide: true,
    widthClass: 'w-32',
  },
  org_count: {
    id: 'org_count',
    label: 'Orgs',
    visible: true,
    sortable: true,
    order: 11,
    canHide: true,
    widthClass: 'w-16',
  },
  campaign_type: {
    id: 'campaign_type',
    label: 'Type',
    visible: false, // Hidden by default
    sortable: true,
    order: 12,
    canHide: true,
    widthClass: 'w-24',
  },
  due_date: {
    id: 'due_date',
    label: 'Due Date',
    visible: false, // Hidden by default
    sortable: true,
    order: 13,
    canHide: true,
    widthClass: 'w-32',
  },
  created_at: {
    id: 'created_at',
    label: 'Created',
    visible: false, // Hidden by default
    sortable: true,
    order: 14,
    canHide: true,
    widthClass: 'w-32',
  },
  start_location: {
    id: 'start_location',
    label: 'Start Location',
    visible: true, // Visible by default
    sortable: false,
    order: 15,
    canHide: true,
    widthClass: 'w-48',
  },
  end_location: {
    id: 'end_location',
    label: 'End Location',
    visible: true, // Visible by default
    sortable: false,
    order: 16,
    canHide: true,
    widthClass: 'w-48',
  },
  actions: {
    id: 'actions',
    label: 'Actions',
    visible: true,
    sortable: false,
    order: 17,
    canHide: false, // Always visible
    widthClass: 'w-10',
  },
};

/**
 * Default column configurations for Organization table
 */
const DEFAULT_ORGANIZATION_COLUMNS: TableColumnState = {
  select: {
    id: 'select',
    label: 'Select',
    visible: true,
    sortable: false,
    order: 0,
    canHide: false, // Always visible
    widthClass: 'w-8',
  },
  number: {
    id: 'number',
    label: '#',
    visible: true,
    sortable: false,
    order: 1,
    canHide: true,
    hideOnMobile: true,
    widthClass: 'w-10',
  },
  name: {
    id: 'name',
    label: 'Company Name',
    visible: true,
    sortable: true,
    order: 2,
    canHide: true,
  },
  address: {
    id: 'address',
    label: 'Address',
    visible: true,
    sortable: false,
    order: 3,
    canHide: true,
    hideOnMobile: true,
  },
  city: {
    id: 'city',
    label: 'City',
    visible: false, // Hidden by default (included in address)
    sortable: true,
    order: 4,
    canHide: true,
  },
  quadrant: {
    id: 'quadrant',
    label: 'Quadrant',
    visible: true,
    sortable: true,
    order: 5,
    canHide: true,
    hideOnMobile: true,
    widthClass: 'w-16',
  },
  owner_name: {
    id: 'owner_name',
    label: 'Owner',
    visible: false, // Hidden by default
    sortable: true,
    order: 6,
    canHide: true,
    widthClass: 'w-32',
  },
  owner_email: {
    id: 'owner_email',
    label: 'Owner Email',
    visible: false, // Hidden by default
    sortable: false,
    order: 7,
    canHide: true,
    widthClass: 'w-48',
  },
  next_activity_date: {
    id: 'next_activity_date',
    label: 'Next Activity',
    visible: false, // Hidden by default
    sortable: true,
    order: 8,
    canHide: true,
    widthClass: 'w-32',
  },
  next_activity_type: {
    id: 'next_activity_type',
    label: 'Activity Type',
    visible: false, // Hidden by default
    sortable: false,
    order: 9,
    canHide: true,
    widthClass: 'w-32',
  },
};

/**
 * Store interface for table column management
 */
interface TableColumnStore {
  // State
  campaigns: TableColumnState;
  organizations: TableColumnState;

  // Column visibility actions
  toggleColumn: (tableType: TableType, columnId: string) => void;
  showColumn: (tableType: TableType, columnId: string) => void;
  hideColumn: (tableType: TableType, columnId: string) => void;

  // Bulk actions
  showAllColumns: (tableType: TableType) => void;
  hideAllColumns: (tableType: TableType) => void; // Respects canHide
  resetToDefaults: (tableType: TableType) => void;

  // Column reordering (for future enhancement)
  reorderColumns: (tableType: TableType, newOrder: string[]) => void;

  // Column width adjustment (for future enhancement)
  setColumnWidth: (tableType: TableType, columnId: string, width: number) => void;

  // Utility
  getVisibleColumns: (tableType: TableType) => ColumnConfig[];
  getHiddenColumns: (tableType: TableType) => ColumnConfig[];
  getHiddenCount: (tableType: TableType) => number;
}

/**
 * Table Column Store
 *
 * Persists to localStorage with key 'dms-table-columns-storage'
 */
export const useTableColumnStore = create<TableColumnStore>()(
  persist(
    (set, get) => ({
      // Initial state
      campaigns: DEFAULT_CAMPAIGN_COLUMNS,
      organizations: DEFAULT_ORGANIZATION_COLUMNS,

      // Toggle column visibility
      toggleColumn: (tableType, columnId) => {
        set((state) => ({
          [tableType]: {
            ...state[tableType],
            [columnId]: {
              ...state[tableType][columnId],
              visible: !state[tableType][columnId].visible,
            },
          },
        }));
      },

      // Show a specific column
      showColumn: (tableType, columnId) => {
        set((state) => ({
          [tableType]: {
            ...state[tableType],
            [columnId]: {
              ...state[tableType][columnId],
              visible: true,
            },
          },
        }));
      },

      // Hide a specific column (if canHide is true)
      hideColumn: (tableType, columnId) => {
        const column = get()[tableType][columnId];
        if (!column.canHide) return; // Don't hide if canHide is false

        set((state) => ({
          [tableType]: {
            ...state[tableType],
            [columnId]: {
              ...state[tableType][columnId],
              visible: false,
            },
          },
        }));
      },

      // Show all columns for a table
      showAllColumns: (tableType) => {
        set((state) => {
          const columns = state[tableType];
          const updatedColumns: TableColumnState = {};

          Object.keys(columns).forEach((columnId) => {
            updatedColumns[columnId] = {
              ...columns[columnId],
              visible: true,
            };
          });

          return {
            [tableType]: updatedColumns,
          };
        });
      },

      // Hide all columns for a table (respects canHide flag)
      hideAllColumns: (tableType) => {
        set((state) => {
          const columns = state[tableType];
          const updatedColumns: TableColumnState = {};

          Object.keys(columns).forEach((columnId) => {
            const column = columns[columnId];
            updatedColumns[columnId] = {
              ...column,
              visible: column.canHide ? false : column.visible,
            };
          });

          return {
            [tableType]: updatedColumns,
          };
        });
      },

      // Reset columns to default configuration
      resetToDefaults: (tableType) => {
        const defaults =
          tableType === 'campaigns'
            ? DEFAULT_CAMPAIGN_COLUMNS
            : DEFAULT_ORGANIZATION_COLUMNS;

        set({
          [tableType]: defaults,
        });
      },

      // Reorder columns by providing new order array of columnIds
      reorderColumns: (tableType, newOrder) => {
        set((state) => {
          const columns = state[tableType];
          const updatedColumns: TableColumnState = {};

          newOrder.forEach((columnId, index) => {
            if (columns[columnId]) {
              updatedColumns[columnId] = {
                ...columns[columnId],
                order: index,
              };
            }
          });

          return {
            [tableType]: updatedColumns,
          };
        });
      },

      // Set column width
      setColumnWidth: (tableType, columnId, width) => {
        set((state) => ({
          [tableType]: {
            ...state[tableType],
            [columnId]: {
              ...state[tableType][columnId],
              width,
            },
          },
        }));
      },

      // Get visible columns sorted by order
      getVisibleColumns: (tableType) => {
        const columns = get()[tableType];
        return Object.values(columns)
          .filter((col) => col.visible)
          .sort((a, b) => a.order - b.order);
      },

      // Get hidden columns sorted by order
      getHiddenColumns: (tableType) => {
        const columns = get()[tableType];
        return Object.values(columns)
          .filter((col) => !col.visible)
          .sort((a, b) => a.order - b.order);
      },

      // Get count of hidden columns
      getHiddenCount: (tableType) => {
        const columns = get()[tableType];
        return Object.values(columns).filter((col) => !col.visible && col.canHide).length;
      },
    }),
    {
      name: 'dms-table-columns-storage', // localStorage key
      partialize: (state) => ({
        campaigns: state.campaigns,
        organizations: state.organizations,
      }),
      // Merge function to handle new columns added to defaults
      // This ensures localStorage-persisted state includes any new columns
      merge: (persistedState: unknown, currentState: TableColumnStore) => {
        const persisted = persistedState as Partial<AllTableColumnsState>;

        // Merge campaigns columns - add any missing columns from defaults
        const mergedCampaigns = {
          ...DEFAULT_CAMPAIGN_COLUMNS,
          ...(persisted?.campaigns || {}),
        };

        // Force start_location and end_location to be visible (migration for existing users)
        if (mergedCampaigns.start_location) {
          mergedCampaigns.start_location = {
            ...mergedCampaigns.start_location,
            visible: true,
          };
        }
        if (mergedCampaigns.end_location) {
          mergedCampaigns.end_location = {
            ...mergedCampaigns.end_location,
            visible: true,
          };
        }

        // Merge organizations columns - add any missing columns from defaults
        const mergedOrganizations = {
          ...DEFAULT_ORGANIZATION_COLUMNS,
          ...(persisted?.organizations || {}),
        };

        return {
          ...currentState,
          campaigns: mergedCampaigns,
          organizations: mergedOrganizations,
        };
      },
    }
  )
);

// ==================== Cloud Sync ====================

// Cloud sync configuration
const tableColumnCloudSync = createCloudSync<{
  campaigns: TableColumnState;
  organizations: TableColumnState;
}>({
  key: 'table-column-settings',
  namespace: 'ui-preferences',
  debounceMs: 1000,
});

/**
 * Hook to subscribe to store changes for cloud synchronization
 * Call this in your root app component to enable auto-sync
 */
export function useTableColumnStoreCloudSync() {
  useEffect(() => {
    const unsubscribe = useTableColumnStore.subscribe((state) => {
      tableColumnCloudSync.syncToCloud({
        campaigns: state.campaigns,
        organizations: state.organizations,
      });
    });
    return () => unsubscribe();
  }, []);
}

/**
 * Load from cloud on app initialization
 * Call this during app startup to restore preferences from PostgreSQL
 */
export async function initTableColumnsFromCloud(): Promise<void> {
  const cloudState = await tableColumnCloudSync.loadFromCloud();
  if (cloudState) {
    useTableColumnStore.setState(cloudState);
  }
}
